/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { useSession } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar 
} from 'recharts';
import MarketingMetrics from '../components/dashboard/marketing/MarketingMetrics';
import MarketingJobPoolPreview from '../components/dashboard/marketing/MarketingJobPoolPreview';
import MarketingAssignedJobs from '../components/dashboard/marketing/MarketingAssignedJobs';
import MarketingPerformanceChart from '../components/dashboard/marketing/MarketingPerformanceChart';
import UnifiedFeed from '../components/dashboard/UnifiedFeed';
// Removed DashboardHero import
import CountCard from '../components/dashboard/CountCard';
import QuickResumeCard from '../components/dashboard/QuickResumeCard';
import NextBestActionCard from '../components/dashboard/NextBestActionCard';
import PerformanceChart from '../components/dashboard/PerformanceChart';
import NextLearningStep from '../components/dashboard/NextLearningStep';

export default function Dashboard() {
  const { data: session } = useSession();
  const role = (session?.user as any)?.role || "STUDENT";
  const userId = session?.user?.id;
  const userName = session?.user?.name || "Member";

  // State values
  const [personalStats, setPersonalStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const [adminStats, setAdminStats] = useState<any>(null);
  const [coachStats, setCoachStats] = useState({ pending: 0, threads: 0, desk: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const triggerRefresh = () => setRefreshTrigger(prev => prev + 1);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (role === 'STUDENT') {
          // Fetch student metrics
          const analyticsRes = await api.get(`/analytics/personal?userId=${userId}`);
          setPersonalStats(analyticsRes.data);
        } else if (role === 'ADMIN') {
          // Fetch admin statistics
          const aiUsageRes = await api.get('/admin/ai-usage');
          setAdminStats(aiUsageRes.data);
          
          // Generate mock chart data for student growth
          setChartData([
            { name: 'Week 1', Users: 12 },
            { name: 'Week 2', Users: 24 },
            { name: 'Week 3', Users: 48 },
            { name: 'Week 4', Users: 95 },
          ]);
        } else if (role === 'COACH' || role === 'COACHING_ASSOCIATE') {
          // Fetch dynamic stats for Coach Dashboard
          const [subsRes, threadsRes, poolRes] = await Promise.all([
            api.get(`/staff/all-submissions?userId=${userId}`),
            api.get(`/discussions/unread-count/${userId}`),
            api.get(`/service-pool?userId=${userId}`)
          ]);
          
          const pendingCount = (subsRes.data || []).filter((s: any) => s.status === 'PENDING').length;
          const threadsCount = threadsRes.data?.count || 0;
          const deskCount = (poolRes.data || []).filter((p: any) => p.status === 'OPEN' || p.status === 'ASSIGNED').length;
          
          setCoachStats({ pending: pendingCount, threads: threadsCount, desk: deskCount });
        }
      } catch (err) {
        console.error("Failed to load dashboard statistics:", err);
      } finally {
        setLoading(false);
      }
    };
    if (userId) {
      fetchData();
    }
  }, [role, userId]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-on-surface">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm font-semibold uppercase tracking-widest text-slate-500">Loading Dashboard Panels...</p>
        </div>
      </div>
    );
  }

  // --- RENDER DUAL-MODE PANELS ---

  const renderStudentPanel = () => {
    return (
      <div className="space-y-8 max-w-7xl mx-auto pb-12 w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="grid grid-cols-12 gap-8">
          {/* Left Section (Column Span 8) */}
          <div className="col-span-12 lg:col-span-8 space-y-8 text-left">
            
            {/* New: Next Learning Step Mission Card (now absorbing Hero) */}
            <NextLearningStep stats={personalStats} loading={loading} />
            
            {/* 2. Compact Quick-Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <NextBestActionCard action={personalStats?.nextBestAction} />
              <CountCard 
                title="Action Required" 
                count={personalStats?.escalations || 0} 
                label="Urgent" 
                icon="report" 
                colorClass="text-red-600"
                bgClass="bg-red-50 dark:bg-red-900/20"
                loading={loading}
              />
            </div>

            {/* 3. Latest Class (Quick Access) */}
            <div className="h-auto md:h-[280px]">
               <QuickResumeCard />
            </div>

            {/* 4. Total Score Graph */}
            <PerformanceChart data={personalStats?.history || []} loading={loading} currentPoints={personalStats?.points || 0} />

          </div>

          {/* Right Section (Column Span 4) - The Interaction Sidebar */}
          <div className="col-span-12 lg:col-span-4 space-y-8 text-left">
            {/* 6. Feeds (Unified Feed) */}
            <UnifiedFeed />
          </div>
        </div>
      </div>
    );
  };

  const renderAdminPanel = () => {
    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 bg-surface-container-lowest p-8 rounded-[2rem] border border-outline-variant/10 shadow-sm">
          <div>
            <h1 className="text-3xl font-black text-on-surface tracking-tight">
              Curator Portal <span className="brand-gradient">Control.</span>
            </h1>
            <p className="text-sm font-bold text-on-surface-variant mt-1">
              Active Admin: <span className="text-slate-900 dark:text-slate-100">{userName}</span>
            </p>
          </div>
        </div>

        {/* Admin Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-surface-container-lowest border border-outline-variant/10 p-6 rounded-[2rem] shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Total API Calls</span>
            <h2 className="text-3xl font-black text-on-surface mt-2">{adminStats?.totalCalls || 1402}</h2>
            <p className="text-[10px] text-emerald-600 font-bold mt-1">â†‘ 12.5% this month</p>
          </div>
          <div className="bg-surface-container-lowest border border-outline-variant/10 p-6 rounded-[2rem] shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Avg Response Time</span>
            <h2 className="text-3xl font-black text-on-surface mt-2">240ms</h2>
            <p className="text-[10px] text-slate-400 font-bold mt-1">Sanctum Auth Latency</p>
          </div>
          <div className="bg-surface-container-lowest border border-outline-variant/10 p-6 rounded-[2rem] shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">System Tokens</span>
            <h2 className="text-3xl font-black text-on-surface mt-2">{adminStats?.tokensSpent || 98200}</h2>
            <p className="text-[10px] text-indigo-500 font-bold mt-1">AI usage balance</p>
          </div>
          <div className="bg-surface-container-lowest border border-outline-variant/10 p-6 rounded-[2rem] shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Active Students</span>
            <h2 className="text-3xl font-black text-on-surface mt-2">482</h2>
            <p className="text-[10px] text-emerald-600 font-bold mt-1">â†‘ 22 users online</p>
          </div>
        </div>

        {/* Charts & Graphs and Feed */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-surface-container-lowest border border-outline-variant/10 p-6 rounded-[2rem] shadow-sm">
            <h3 className="text-sm font-black uppercase tracking-wider text-on-surface mb-6">Student Growth Metrics</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="name" stroke="#6c7a71" fontSize={10} />
                  <YAxis stroke="#6c7a71" fontSize={10} />
                  <Tooltip />
                  <Line type="monotone" dataKey="Users" stroke="#006c49" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-surface-container-lowest border border-outline-variant/10 p-6 rounded-[2rem] shadow-sm">
            <h3 className="text-sm font-black uppercase tracking-wider text-on-surface mb-6">AI Prompt Generation Hits</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Reinventions', count: 124 },
                  { name: 'Audiences', count: 85 },
                  { name: 'Positionings', count: 215 },
                  { name: 'Media Posters', count: 68 },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="name" stroke="#6c7a71" fontSize={10} />
                  <YAxis stroke="#6c7a71" fontSize={10} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#10b981" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
            </div>
          </div>
          <div className="lg:col-span-4">
            <UnifiedFeed />
          </div>
        </div>
      </div>
    );
  };

  const renderCoachPanel = () => {
    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="bg-surface-container-lowest p-8 rounded-[2rem] border border-outline-variant/10 shadow-sm">
          <h1 className="text-3xl font-black text-on-surface tracking-tight">
            Coach Dashboard <span className="brand-gradient">Control.</span>
          </h1>
          <p className="text-sm font-bold text-on-surface-variant mt-1">
            Active Educator: <span className="text-slate-900 dark:text-slate-100">{userName}</span>
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link to="/taskapprovals" className="bg-surface-container-lowest border border-outline-variant/10 p-6 rounded-[2rem] shadow-sm flex flex-col justify-between hover:bg-surface-container-low cursor-pointer transition-colors group">
            <div>
              <span className="material-symbols-outlined text-3xl text-primary mb-3 group-hover:scale-110 transition-transform">fact_check</span>
              <h3 className="text-lg font-black text-on-surface">Pending Submissions</h3>
              <p className="text-xs text-on-surface-variant leading-relaxed mt-2">
                Evaluate student strategies and assign scores and rank points.
              </p>
            </div>
            <div className="mt-6 flex justify-between items-end">
              <span className="text-3xl font-black text-on-surface">{coachStats.pending}</span>
              <span className="text-[10px] font-black uppercase text-primary bg-primary/10 px-2 py-1 rounded-md">Review Required</span>
            </div>
          </Link>

          <Link to="/discussions" className="bg-surface-container-lowest border border-outline-variant/10 p-6 rounded-[2rem] shadow-sm flex flex-col justify-between hover:bg-surface-container-low cursor-pointer transition-colors group">
            <div>
              <span className="material-symbols-outlined text-3xl text-indigo-500 mb-3 group-hover:scale-110 transition-transform">forum</span>
              <h3 className="text-lg font-black text-on-surface">Active Threads</h3>
              <p className="text-xs text-on-surface-variant leading-relaxed mt-2">
                Conversations and task discussions on student submissions.
              </p>
            </div>
            <div className="mt-6 flex justify-between items-end">
              <span className="text-3xl font-black text-on-surface">{coachStats.threads}</span>
              <span className="text-[10px] font-black uppercase text-indigo-600 bg-indigo-50 px-2 py-1 rounded-md">Unread comments</span>
            </div>
          </Link>

          <Link to="/coach/desk" className="bg-surface-container-lowest border border-outline-variant/10 p-6 rounded-[2rem] shadow-sm flex flex-col justify-between hover:bg-surface-container-low cursor-pointer transition-colors group">
            <div>
              <span className="material-symbols-outlined text-3xl text-cyan-500 mb-3 group-hover:scale-110 transition-transform">support_agent</span>
              <h3 className="text-lg font-black text-on-surface">Service Pool Desk</h3>
              <p className="text-xs text-on-surface-variant leading-relaxed mt-2">
                Coordinate booked execution calls and schedule slots.
              </p>
            </div>
            <div className="mt-6 flex justify-between items-end">
              <span className="text-3xl font-black text-on-surface">{coachStats.desk}</span>
              <span className="text-[10px] font-black uppercase text-cyan-600 bg-cyan-50 px-2 py-1 rounded-md">Booked slots</span>
            </div>
          </Link>
            </div>
          </div>
          <div className="lg:col-span-4">
            <UnifiedFeed />
          </div>
        </div>
      </div>
    );
  };


  const renderMarketingPanel = () => {
    return (
      <div className="space-y-12 animate-in fade-in slide-in-from-bottom-6 duration-1000 pb-20">
        {/* Page Title & Status */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 px-2 text-left">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-500 bg-orange-500/10 px-3 py-1 rounded-full">Systems Online</span>
              <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-on-surface tracking-tighter leading-tight italic">
              Marketing <span className="text-orange-500">Execution.</span>
            </h2>
            <p className="text-sm font-medium text-on-surface-variant/60">Professional fulfillment workstation and performance hub.</p>
          </div>
        </div>

        {/* Overview Metrics */}
        <MarketingMetrics />

        {/* Main Production Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start text-left">
          {/* Left Column: Job Flow (8 Cols) */}
          <div className="lg:col-span-8 space-y-12">
            <MarketingAssignedJobs refreshTrigger={refreshTrigger} />
            <MarketingJobPoolPreview onClaim={triggerRefresh} />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
               <div className="bg-surface-container-low p-8 rounded-[2.5rem] border border-outline-variant/10 shadow-sm relative overflow-hidden group border-b-8 border-purple-500">
                 <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:scale-110 transition-transform">
                   <span className="material-symbols-outlined text-6xl text-purple-600 fill-1">campaign</span>
                 </div>
                 <h3 className="text-xl font-black text-on-surface tracking-tighter mb-2">Campaign Ops</h3>
                 <p className="text-sm font-medium text-on-surface-variant opacity-60 leading-relaxed">
                   Coordinate active student referral programs and multi-channel acquisition funnels.
                 </p>
                 <button className="mt-6 flex items-center gap-2 text-[10px] font-black uppercase text-purple-600 tracking-widest hover:translate-x-1 transition-transform cursor-pointer">
                   Open Management <span className="material-symbols-outlined text-sm">arrow_forward</span>
                 </button>
               </div>

               <div className="bg-surface-container-low p-8 rounded-[2.5rem] border border-outline-variant/10 shadow-sm relative overflow-hidden group border-b-8 border-emerald-500">
                 <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:scale-110 transition-transform">
                   <span className="material-symbols-outlined text-6xl text-emerald-600 fill-1">monitoring</span>
                 </div>
                 <h3 className="text-xl font-black text-on-surface tracking-tighter mb-2">Conversion Hub</h3>
                 <p className="text-sm font-medium text-on-surface-variant opacity-60 leading-relaxed">
                   Real-time fulfillment tracking and student success conversion metrics.
                 </p>
                 <button className="mt-6 flex items-center gap-2 text-[10px] font-black uppercase text-emerald-600 tracking-widest hover:translate-x-1 transition-transform cursor-pointer">
                   Analyze Data <span className="material-symbols-outlined text-sm">arrow_forward</span>
                 </button>
               </div>
            </div>

            <div className="mt-12 h-[400px]">
              <MarketingPerformanceChart />
            </div>
          </div>

          {/* Right Column: Community & Social (4 Cols) */}
          <div className="lg:col-span-4 sticky top-10 h-[calc(100vh-160px)]">
            <UnifiedFeed />
          </div>
        </div>
      </div>
    );
  };

  // Switch display based on logged-in user role
  switch (role) {
    case "ADMIN":
      return renderAdminPanel();
    case "COACH":
    case "COACHING_ASSOCIATE":
      return renderCoachPanel();
    case "MARKETING_ASSOCIATE":
      return renderMarketingPanel();
    case "STUDENT":
    default:
      return renderStudentPanel();
  }
}
